import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  useOrganizationPreboardingSettings,
  useOrganizationUpdatePreboardingSettings,
} from "@/queries/settings/use-organization.query.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Page, Skeleton, Stack, Switch, Title, useToasts } from "@skailer-com/uikit";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const schema = z.object({
  show_manager: z.boolean(),
});

type Schema = z.infer<typeof schema>;

export default function PreboardingSettings() {
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { data: settings, isLoading } = useOrganizationPreboardingSettings();

  useTitle(t("core:settings_preboarding_title"), t("common:settings"));

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<Schema>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: { show_manager: true },
  });

  const updatePreboardingSettings = useOrganizationUpdatePreboardingSettings(control);

  useEffect(() => {
    if (settings) {
      reset({ show_manager: settings.show_manager });
    }
  }, [settings, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updatePreboardingSettings.mutateAsync({ show_manager: data.show_manager });
      showInfo({ title: t("common:you_changes_were_saved") });
    } catch {
      return;
    }
  });

  if (isLoading) {
    return (
      <Page>
        <Page.Header
          title={t("core:settings_preboarding_title")}
          showBack={true}
          navigateTo={"/settings/general"}
        />
        <Page.Content>
          <Stack className={"container-sm mx-auto"} gap={"lg"}>
            <Skeleton className={"h-32 w-full"} />
            <Skeleton className={"h-32 w-full"} />
          </Stack>
        </Page.Content>
      </Page>
    );
  }

  return (
    <Page>
      <Page.Header
        title={t("core:settings_preboarding_title")}
        showBack={true}
        navigateTo={"/settings/general"}
      />
      <Page.Content>
        <Stack gap={"xl"} className={"container-sm mx-auto"}>
          <Title
            size={"lg"}
            paddingTop
            paddingBottom
            header={t("core:settings_preboarding_title")}
            caption={t("core:settings_preboarding_description")}
          />

          <Title
            size={"xs"}
            header={t("core:preboarding_settings_always_shown_title")}
            caption={t("core:preboarding_settings_always_shown_description")}
          />

          <form onSubmit={onSubmit}>
            <Stack gap={"lg"}>
              <Controller
                control={control}
                name={"show_manager"}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    size={"sm"}
                    checked={value}
                    onChange={onChange}
                    title={t("core:preboarding_settings_show_manager")}
                    description={t("core:preboarding_settings_show_manager_description")}
                  />
                )}
              />
              <ServerErrorField errors={errors} />
              <Button size={"lg"} type={"submit"} isLoading={updatePreboardingSettings.isPending}>
                {t("common:save")}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Page.Content>
    </Page>
  );
}
